/**
 * CommunityHub — "See what's happening in {city}" widget for the
 * LocationArticle page. Renders 5 tabs of live/auto-refreshing local data:
 *
 *   Pulse   — user posts filtered by city
 *   Events  — user events + Wikipedia-ingested annual festivals
 *   Causes  — open help requests filtered by city
 *   News    — headlines filtered by city / country
 *   Weather — wttr.in current conditions + 3-day forecast (6h cache)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Loader2, RadioTower, CalendarDays, HeartHandshake, Newspaper,
  CloudSun, ExternalLink, MapPin, ShoppingBag, Users2, ArrowUpRight,
} from "lucide-react";
import PlaceStamp from "@/components/PlaceStamp";
import PlaceKindTag from "@/components/PlaceKindTag";

// The first 5 tabs render inline panels of live/local data. The last 2
// ("nav" kind) are cross-app links that navigate to full pages —
// Marketplace + Family Trees are big-enough sections to warrant their
// own dedicated pages, but users expect them to appear in this "what's
// happening locally" hub because they're inherently location-scoped.
const TAB_CONFIG = [
  { key: "pulse",       label: "Neighborhood Pulse", Icon: RadioTower },
  { key: "events",      label: "Events & Festivals", Icon: CalendarDays },
  { key: "causes",      label: "Open Causes",        Icon: HeartHandshake },
  { key: "news",        label: "Local News",         Icon: Newspaper },
  { key: "weather",     label: "Weather",            Icon: CloudSun },
  { key: "marketplace", label: "Marketplace",        Icon: ShoppingBag, kind: "nav", to: "/marketplace" },
  { key: "family",      label: "Family Trees",       Icon: Users2,      kind: "nav", to: "/family" },
];

function EmptyRow({ text }) {
  return (
    <div className="py-8 text-center text-sm opacity-60" data-testid="community-empty">
      {text}
    </div>
  );
}

/**
 * Big, prominent CTA that replaces both the small guest banner AND
 * the empty-list message when a tab has no items yet.  This is the
 * single strongest call-to-action on the article page — it converts
 * "empty tab" into "prime posting real estate".
 *
 * Signed-in users → deep-link into the composer (with `?city=` prefill).
 * Guests         → deep-link to register with a redirect back.
 */
function EmptyActionCta({ cityName, targetPath, guestRedirect, verb, kind }) {
  const { user } = useAuth() || {};
  const isGuest = !user;
  const cityWithPrefix =
    encodeURIComponent(cityName || "");
  const href = isGuest
    ? `/register?redirect=${encodeURIComponent(guestRedirect)}`
    : `${targetPath}?city=${cityWithPrefix}`;

  return (
    <Link
      to={href}
      data-testid={`community-empty-cta-${kind}`}
      className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-dashed text-sm transition hover:shadow-sm hover:-translate-y-0.5"
      style={{
        borderColor: "#4338CA55",
        background: "linear-gradient(135deg, #EEF2FF 0%, #FDF4FF 100%)",
        color: "#1E1B4B",
      }}
    >
      <ArrowUpRight className="w-4 h-4" style={{ color: "#4338CA" }} />
      <span>
        Nothing yet in <span className="font-semibold" style={{ color: "#4338CA" }}>{cityName}</span> —
        {isGuest ? " sign up to " : " "}{verb.toLowerCase()}.
      </span>
    </Link>
  );
}

/**
 * Header CTA — a compact "+ Add <verb> in <city>" pill placed on the
 * top bar next to the "Updated …" timestamp. Adapts to whichever tab
 * is currently active so the caller always sees the primary action
 * inline with the location name (instead of a giant empty-state block
 * further down the page).
 */
function HeaderCta({ tab, cityName, guestRedirect, palette }) {
  const { user } = useAuth() || {};
  const isGuest = !user;
  const cityQs = encodeURIComponent(cityName || "");

  // Map tab → verb + composer URL. Tabs not listed (news/weather etc.)
  // return null so the header stays clean when there's nothing to post.
  const CONFIG = {
    pulse:  { verb: "Post a Pulse",  target: "/pulse" },
    events: { verb: "Post an Event", target: "/events/submit" },
    causes: { verb: "Post a Cause",  target: "/submit-cause" },
  };
  const cfg = CONFIG[tab];
  if (!cfg) return null;

  const href = isGuest
    ? `/register?redirect=${encodeURIComponent(guestRedirect)}`
    : `${cfg.target}?city=${cityQs}`;

  return (
    <Link
      to={href}
      data-testid={`community-header-cta-${tab}`}
      className="relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm hover:shadow-md transition"
      style={{ background: palette.label, color: "#fff" }}
    >
      <ArrowUpRight className="w-3.5 h-3.5" />
      {cfg.verb} in {cityName}
    </Link>
  );
}

/**
 * Inline join CTA shown to signed-out visitors at the top of the
 * "postable" tabs (Pulse, Events, Causes).  Uses `?redirect=<current>`
 * so the guest returns to the same article after signing up.  Guest
 * article visits are our highest-intent conversion moment (they see
 * local activity and want in) — this puts the ask right where the
 * intent lives instead of forcing users to hunt for the header.
 */
function GuestJoinCta({ redirect, message }) {
  const target = `/register?redirect=${encodeURIComponent(redirect)}`;
  return (
    <Link
      to={target}
      data-testid="community-guest-cta"
      className="block mb-4 p-4 rounded-xl border transition group hover:shadow-md"
      style={{
        borderColor: "#4338CA33",
        background: "linear-gradient(135deg, #EEF2FF 0%, #FDF4FF 100%)",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "#4338CA", color: "white" }}
        >
          <ArrowUpRight className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#1E1B4B" }}>
            {message}
          </div>
          <div className="text-xs mt-0.5" style={{ color: "#4338CAcc" }}>
            Join Social Pulse — takes ~30 seconds with your phone number.
          </div>
        </div>
        <span
          className="wofy-btn-primary text-sm !py-1.5 !px-3 shrink-0 group-hover:brightness-110"
        >
          Join
        </span>
      </div>
    </Link>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function PulseCard({ item }) {
  return (
    <Link
      to={`/pulse-item/${item.id}`}
      className="block p-3 rounded-lg border hover:shadow-md transition"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid={`community-pulse-${item.id}`}
    >
      <div className="text-xs opacity-60 mb-1">{item.category || "Post"} · {fmtDate(item.created_at)}</div>
      <div className="font-semibold text-sm line-clamp-2">{item.title}</div>
      {item.description && (
        <p className="text-xs opacity-80 mt-1 line-clamp-2">{item.description}</p>
      )}
      {item.user_name && <div className="text-xs opacity-60 mt-2">by {item.user_name}</div>}
    </Link>
  );
}

function EventCard({ item }) {
  const isExternal = item.source && item.source !== "user";
  const url = isExternal ? item.url : (item.url || `/events/${item.id}`);
  const Node = isExternal ? "a" : Link;
  const props = isExternal
    ? { href: url, target: "_blank", rel: "noopener noreferrer" }
    : { to: url };
  return (
    <Node
      {...props}
      className="block p-3 rounded-lg border hover:shadow-md transition"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid={`community-event-${item.id}`}
    >
      <div className="text-xs opacity-60 mb-1 flex items-center gap-1.5">
        {item.starts_at ? fmtDate(item.starts_at) : (item.kind === "annual" ? "Annual event" : "")}
        {item.source && (
          <span className="ml-auto text-[10px] uppercase tracking-widest opacity-60">
            {item.source === "user" ? "COMMUNITY" : item.source}
          </span>
        )}
      </div>
      <div className="font-semibold text-sm line-clamp-2 flex items-center gap-1">
        {item.title}
        {isExternal && <ExternalLink className="w-3 h-3 opacity-60" />}
      </div>
      {item.venue && (
        <div className="text-xs opacity-70 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{item.venue}</div>
      )}
    </Node>
  );
}

function CauseCard({ item }) {
  const urgencyColor = {
    emergency: "#DC2626",
    high:      "#EA580C",
    normal:    "#2563EB",
    low:       "#6B7280",
  }[item.urgency || "normal"];
  return (
    <Link
      to={`/causes/${item.id}`}
      className="block p-3 rounded-lg border hover:shadow-md transition"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid={`community-cause-${item.id}`}
    >
      <div className="text-xs mb-1 flex items-center gap-2">
        <span className="uppercase font-mono tracking-widest px-1.5 py-0.5 rounded" style={{ background: `${urgencyColor}18`, color: urgencyColor }}>
          {item.urgency || "normal"}
        </span>
        <span className="opacity-60">{item.category || "cause"}</span>
      </div>
      <div className="font-semibold text-sm line-clamp-2">{item.title}</div>
      {item.description && <p className="text-xs opacity-80 mt-1 line-clamp-2">{item.description}</p>}
    </Link>
  );
}

function NewsCard({ item }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 rounded-lg border hover:shadow-md transition"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid={`community-news-${item.id}`}
    >
      <div className="text-xs opacity-60 mb-1">{item.source} · {fmtDate(item.published_at)}</div>
      <div className="font-semibold text-sm line-clamp-2 flex items-start gap-1">
        <span>{item.title}</span>
        <ExternalLink className="w-3 h-3 opacity-60 shrink-0 mt-1" />
      </div>
      {item.summary && <p className="text-xs opacity-80 mt-1 line-clamp-2">{item.summary}</p>}
    </a>
  );
}

function WeatherPanel({ weather, city }) {
  if (!weather?.current) return <EmptyRow text={`No live weather for ${city || "this location"}.`} />;
  const cur = weather.current;
  const fc = weather.forecast || [];
  return (
    <div data-testid="community-weather-panel">
      <div className="flex items-center gap-4 p-4 rounded-xl border" style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #EEF2FF 0%, #FEF3C7 100%)" }}>
        <div className="text-5xl font-bold text-slate-800" data-testid="community-weather-temp">{cur.temp_c}°C</div>
        <div className="flex-1">
          <div className="font-semibold text-slate-800">{cur.condition}</div>
          <div className="text-xs opacity-70 text-slate-700">
            Feels {cur.feels_c}°C · Humidity {cur.humidity_pct}% · Wind {cur.wind_kph} kph
          </div>
          <div className="text-[10px] uppercase tracking-widest opacity-50 mt-1 text-slate-700">
            {city} · via {weather.source}
          </div>
        </div>
      </div>
      {fc.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
          {fc.map((d, i) => (
            <div
              key={i}
              className="p-2 rounded-lg border text-center"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid={`community-weather-fc-${i}`}
            >
              <div className="text-xs opacity-70">{fmtDate(d.date)}</div>
              <div className="text-lg font-semibold mt-0.5">{d.max_c}°/{d.min_c}°</div>
              <div className="text-[10px] opacity-70 line-clamp-1">{d.condition || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CommunityHub({ slug, city, headerSlot }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pulse");
  const { user } = useAuth() || {};
  const location = useLocation();
  // Where guests land after signing up — keep it on the current article.
  const redirectAfterAuth = `${location.pathname}${location.search || ""}`;
  const isGuest = !user;

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    http.get(`/community/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const counts = useMemo(() => ({
    pulse:   (data?.pulse || []).length,
    events:  (data?.events || []).length,
    causes:  (data?.causes || []).length,
    news:    (data?.news || []).length,
    weather: data?.weather ? 1 : 0,
  }), [data]);

  const cityName = data?.city || city || "this location";

  // Deterministic per-city palette so every location gets its own look.
  const cityPalette = useMemo(() => {
    const POOL = [
      { bg: "linear-gradient(135deg,#EEF2FF 0%,#E9D5FF 100%)", label: "#4338CA", title: "#1E1B4B", chip: "#EEF2FF", chipText: "#4338CA" },
      { bg: "linear-gradient(135deg,#FFF7ED 0%,#FED7AA 100%)", label: "#9A3412", title: "#431407", chip: "#FFF7ED", chipText: "#9A3412" },
      { bg: "linear-gradient(135deg,#ECFDF5 0%,#A7F3D0 100%)", label: "#065F46", title: "#022C22", chip: "#ECFDF5", chipText: "#065F46" },
      { bg: "linear-gradient(135deg,#FEF3F2 0%,#FECDD3 100%)", label: "#9F1239", title: "#4C0519", chip: "#FEF3F2", chipText: "#9F1239" },
      { bg: "linear-gradient(135deg,#EFF6FF 0%,#BFDBFE 100%)", label: "#1D4ED8", title: "#0B234F", chip: "#EFF6FF", chipText: "#1D4ED8" },
      { bg: "linear-gradient(135deg,#FEFCE8 0%,#FDE68A 100%)", label: "#854D0E", title: "#3B1D0D", chip: "#FEFCE8", chipText: "#854D0E" },
    ];
    let h = 5381;
    for (let i = 0; i < cityName.length; i++) h = ((h << 5) + h + cityName.charCodeAt(i)) | 0;
    return POOL[Math.abs(h) % POOL.length];
  }, [cityName]);

  if (loading) {
    return (
      <div className="my-12 py-8 text-center" data-testid="community-hub-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto opacity-50" />
      </div>
    );
  }

  return (
    <section
      className="my-6 rounded-2xl border overflow-hidden"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid="community-hub"
    >
      {/* Header — graphical, per-city palette with the location name in bold display type.
          The whole coloured band is clickable → jumps to the location's main article
          UNLESS a `headerSlot` is provided (which itself contains links). Nested
          <Link>s emit invalid HTML so in that case we downgrade the wrapper to a
          plain <div>. */}
      {(() => {
        const HEADER_TAG = headerSlot ? "div" : Link;
        const headerProps = headerSlot
          ? {}
          : {
              to: `/l/${slug}`,
              "aria-label": `Open ${cityName} article`,
              "data-testid": "community-hub-header-link",
            };
        return (
          <HEADER_TAG
            {...headerProps}
            className="relative px-5 py-6 sm:py-7 flex items-center justify-between flex-wrap gap-3 overflow-hidden group"
            style={{ background: cityPalette.bg }}
          >
            <span aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-40" style={{ background: cityPalette.label }} />
            <span aria-hidden className="pointer-events-none absolute -bottom-14 -left-8 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: cityPalette.label }} />
            <div className="relative flex items-center gap-3" style={{ color: cityPalette.title }}>
              {/* Illustrated place stamp — deterministic per slug so
                  every city/state gets the same face across visits. */}
              {slug && (() => {
                const segs = slug.split("/").filter(Boolean);
                const last = (segs[segs.length - 1] || "").toLowerCase();
                const kind = /-county$|-parish$|-borough$/.test(last)
                  ? "county"
                  : segs.length === 1 ? "country"
                  : segs.length === 2 ? "state"
                  : "city";
                return (
                  <PlaceStamp
                    kind={kind}
                    slug={slug}
                    size={72}
                    className="shadow-md ring-2 ring-white/70 hidden sm:block"
                    title={cityName}
                    testid="community-hub-stamp"
                  />
                );
              })()}
              <div>
              <span
                className="inline-block text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-full"
                style={{ background: cityPalette.chip, color: cityPalette.chipText }}
              >
                LIVE · LOCAL
              </span>
              {/* Big city name — a <Link> whenever we're inside a
                  headerSlot (wrapper is a div) so at least the title
                  is clickable back to the location home. */}
              <h3
                className="mt-2 font-display leading-none tracking-tight text-4xl sm:text-5xl md:text-6xl group-hover:underline decoration-2 underline-offset-4"
                data-testid="community-hub-title"
                style={{ color: cityPalette.title }}
              >
                {headerSlot ? (
                  <Link
                    to={`/l/${slug}`}
                    className="hover:underline decoration-2 underline-offset-4"
                    data-testid="community-hub-title-link"
                  >
                    {cityName}
                  </Link>
                ) : (
                  cityName
                )}
              </h3>
              <p className="mt-2 text-base font-medium opacity-85" style={{ color: cityPalette.title }}>
                See what's happening
              </p>
              {slug && (() => {
                const segs = slug.split("/").filter(Boolean);
                const last = (segs[segs.length - 1] || "").toLowerCase();
                const kind = /-county$|-parish$|-borough$/.test(last)
                  ? "county"
                  : segs.length === 1 ? "country"
                  : segs.length === 2 ? "state"
                  : "city";
                return (
                  <div className="mt-2">
                    <PlaceKindTag kind={kind} slug={slug} testid="community-hub-kind-tag" />
                  </div>
                );
              })()}
              {headerSlot && <div className="mt-3">{headerSlot}</div>}
              </div>
            </div>
            {/* Right side of the header: compact contextual CTA + timestamp.
                Both live OUTSIDE the wrapper Link click so their own actions win. */}
            <div className="relative flex items-center gap-3 ml-auto" onClick={(e) => e.stopPropagation()}>
              <HeaderCta
                tab={tab}
                cityName={cityName}
                guestRedirect={redirectAfterAuth}
                palette={cityPalette}
              />
              {data?.fetched_at && (
                <div className="text-xs opacity-70 whitespace-nowrap" style={{ color: cityPalette.title }}>
                  Updated {fmtDate(data.fetched_at)}
                </div>
              )}
            </div>
          </HEADER_TAG>
        );
      })()}

      {/* Tabs — first 5 are inline data panels, last 2 are cross-app nav links */}
      <div className="flex gap-1 px-3 pt-3 border-b overflow-x-auto" style={{ borderColor: "var(--wofy-border)" }}>
        {TAB_CONFIG.map((t) => {
          if (t.kind === "nav") {
            // External link tab — deep-link to the section's own page.
            // Pass the current city as a query param so the target page
            // can pre-filter to the same locality (Marketplace / Family
            // Trees both honour it when present, ignore it otherwise).
            const href = `${t.to}?city=${encodeURIComponent(cityName)}`;
            return (
              <Link
                key={t.key}
                to={href}
                className="px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 border-transparent whitespace-nowrap transition hover:opacity-80"
                style={{ color: "#4338CA", fontWeight: 600 }}
                data-testid={`community-tab-${t.key}`}
              >
                <t.Icon className="w-4 h-4" /> {t.label}
                <ArrowUpRight className="w-3 h-3 opacity-70" />
              </Link>
            );
          }
          const active = tab === t.key;
          const n = counts[t.key] || 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 whitespace-nowrap transition"
              style={active
                ? { borderColor: "#4338CA", color: "#4338CA", fontWeight: 600 }
                : { borderColor: "transparent", color: "var(--wofy-text-soft)" }}
              data-testid={`community-tab-${t.key}`}
            >
              <t.Icon className="w-4 h-4" /> {t.label}
              {n > 0 && (
                <span className="ml-1 text-[10px] rounded-full px-1.5 py-0.5" style={{ background: active ? "#4338CA22" : "var(--wofy-border)" }}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      <div className="p-4 min-h-[120px]">
        {tab === "pulse" && (
          <>
            {(data?.pulse || []).length === 0 ? (
              <EmptyActionCta
                cityName={cityName}
                targetPath="/pulse"
                guestRedirect={redirectAfterAuth}
                verb="Add a Pulse"
                kind="pulse"
              />
            ) : (
              <>
                {isGuest && (
                  <GuestJoinCta
                    redirect={redirectAfterAuth}
                    message={`Share what's happening in ${cityName}.`}
                  />
                )}
                <div className="grid sm:grid-cols-2 gap-3">{data.pulse.map((p) => <PulseCard key={p.id} item={p} />)}</div>
              </>
            )}
          </>
        )}
        {tab === "events" && (
          <>
            {(data?.events || []).length === 0 ? (
              <EmptyActionCta
                cityName={cityName}
                targetPath="/events/submit"
                guestRedirect={redirectAfterAuth}
                verb="Add an Event"
                kind="event"
              />
            ) : (
              <>
                {isGuest && (
                  <GuestJoinCta
                    redirect={redirectAfterAuth}
                    message={`Post an event happening in ${cityName}.`}
                  />
                )}
                <div className="grid sm:grid-cols-2 gap-3">{data.events.map((e, i) => <EventCard key={e.id || i} item={e} />)}</div>
              </>
            )}
          </>
        )}
        {tab === "causes" && (
          <>
            {(data?.causes || []).length === 0 ? (
              <EmptyActionCta
                cityName={cityName}
                targetPath="/submit-cause"
                guestRedirect={redirectAfterAuth}
                verb="Add a Cause"
                kind="cause"
              />
            ) : (
              <>
                {isGuest && (
                  <GuestJoinCta
                    redirect={redirectAfterAuth}
                    message={`Start a cause for ${cityName}.`}
                  />
                )}
                <div className="grid sm:grid-cols-2 gap-3">{data.causes.map((c) => <CauseCard key={c.id} item={c} />)}</div>
              </>
            )}
          </>
        )}
        {tab === "news" && (
          (data?.news || []).length === 0
            ? <EmptyRow text={`No local news for ${cityName} yet.`} />
            : <div className="grid sm:grid-cols-2 gap-3">{data.news.map((n) => <NewsCard key={n.id} item={n} />)}</div>
        )}
        {tab === "weather" && <WeatherPanel weather={data?.weather} city={cityName} />}
      </div>
    </section>
  );
}
